import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/site";
import BrandMark from "@/components/site/BrandMark";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-brand-line"
          : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container-tight flex items-center justify-between h-20 md:h-24">
        <Link
          to="/"
          data-testid="nav-brand-link"
          className="flex items-center gap-3 group"
          aria-label="Progressive Innovations — Home"
        >
          <BrandMark size={36} className="shrink-0" />
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-[15px] font-semibold text-brand-ink tracking-tight">
              {SITE.brand}
            </span>
            <span className="text-[10px] tracking-[0.22em] uppercase text-brand-accent mt-0.5">
              {SITE.tagline}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10" data-testid="nav-desktop">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors py-2 ${
                  isActive
                    ? "text-brand-ink after:content-[''] after:absolute after:left-0 after:-bottom-0.5 after:h-px after:w-full after:bg-brand-accent"
                    : "text-brand-muted hover:text-brand-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-book-call"
            className="hidden md:inline-flex btn-primary"
          >
            Book a Discovery Call
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>

          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center border border-brand-line text-brand-ink hover:bg-brand-surface3 transition-colors"
          >
            {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="nav-mobile-drawer"
        className={`lg:hidden overflow-hidden border-t border-brand-line bg-white transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="container-tight py-8 flex flex-col gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `py-4 border-b border-brand-line text-lg font-display font-medium tracking-tight ${
                  isActive ? "text-brand-accent" : "text-brand-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-mobile-cta-book-call"
            className="btn-primary mt-6 w-full"
          >
            Book a Discovery Call
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </header>
  );
}
